import { RefObject } from "react";
import pageBuilderStore from "./pageBuilderStore";
import {ContentBlockType, IDesignBlock} from "../types/iblock";
import contentManager from "./content-manager";
import serializationManager from "./serialization-manager";
import {IHeadingBlockData} from "../types/content-block-data";

/**
 * @class GlideBarManager
 * @description
 * Centralized controller for all GlideBar-related UI state, logic, and state transitions
 * in the PageBuilder application.
 *
 * This manager enforces encapsulation by exposing all allowed mutations and queries as methods,
 * preventing accidental direct state manipulation from components.
 *
 * ## Features:
 * - Handles all state changes related to the GlideBar (visibility, position, target block, dropdowns, etc).
 * - Provides explicit method contracts for updating, resetting, or querying GlideBar state.
 * - Ensures state remains consistent and observable for MobX/reactivity.
 * - Enables future extensibility (undo/redo, audit logs, etc.) by funneling all mutations through this layer.
 *
 * ## Typical Usage:
 * ```ts
 * glideBarManager.setActiveBlock(blockId, blockType, ref);
 * glideBarManager.setDropdownOpen(true);
 * ```
 *
 * @file glidebar-manager.ts
 * @since 2025-06-21
 * @author Chen Weizhi
 */
class GlideBarManager {
	
	getGlideBarState() {
		return pageBuilderStore.glideBarState;
	}
	/**
	 * Sets editing mode for TextBlock. Disables heading mode if active.
	 */
	setIsEditingText(isEditingText: boolean) {
		pageBuilderStore.glideBarState.isEditingText = isEditingText;
		pageBuilderStore.glideBarState.isEditingHeading = false;
	}
	
	/**
	 * Sets editing mode for HeadingBlock. Disables text mode if active.
	 */
	setIsEditingHeading(isEditingHeading: boolean) {
		pageBuilderStore.glideBarState.isEditingText = false;
		pageBuilderStore.glideBarState.isEditingHeading = isEditingHeading;
	}

	/**
	 * Retrieves the current caret (cursor) position as a DOM Range object
	 * within the active editable element in the document.
	 *
	 * @returns {Range | null} A Range object representing the current caret position,
	 *   or null if there is no active selection or the selection is not in an editable element.
	 *
	 * @example
	 * const caretRange = getCurrCaretPos();
	 * if (caretRange) {
	 *   // Use caretRange to insert or format content at the current cursor
	 * }
	 */
	getCurrCaretPos(): Range | null {
		if (pageBuilderStore.glideBarState.currCaretPos != null)
			return pageBuilderStore.glideBarState.currCaretPos;
		return null;
	}

	/**
	 * Restores or sets the caret (cursor) position in the document to the specified Range.
	 * This method is typically used after programmatic DOM changes (such as formatting)
	 * to return the user’s cursor to its previous or intended location.
	 *
	 * @param {Range | null} caretPos - The Range object representing the desired caret position,
	 *   or null to clear any existing selection.
	 *
	 * @example
	 * // Restore caret after formatting or content insertion
	 * setCurrCaretPos(savedCaretRange);
	 */
	setCurrCaretPos(caretPos: Range | null) {
		pageBuilderStore.glideBarState.currCaretPos = caretPos;
	}
	
	/**
	 * Sets the currently active block and enables GlideBar for editing.
	 */
	setActiveBlock(id: string, type: ContentBlockType, ref: RefObject<HTMLElement> | null) {
		pageBuilderStore.glideBarState = {
			...pageBuilderStore.glideBarState,
			isVisible: true,
			contentBlockId: id,
			blockType: type.toLowerCase(),
			contentBlockRef: ref,
		};
	}
	
	/**
	 * Hides the GlideBar and serializes the current block content to JSON.
	 */
	hideGlideBar() {
		pageBuilderStore.glideBarState = {
			...pageBuilderStore.glideBarState,
			isVisible: false,
			contentBlockId: null,
			blockType: null,
			contentBlockRef: null,
		};
		contentManager.setJsonContent(serializationManager.fromBlocksToJSON(contentManager.getDesignContent()));
	}
	
	/**
	 * Toggles the visibility of the heading level dropdown.
	 */
	setDropDownOpen(open: boolean) {
		this.getGlideBarState().isDropDownOpen = open;
	}
	
	/**
	 * Checks the current downdown state
	 * @returns Returns true if the dropdown is open.
	 */
	getDropDownOpen(): boolean {
		return this.getGlideBarState().isDropDownOpen;
	}
	
	/**
	 * Sets the current heading level and updates the target HeadingBlock in-place.
	 */
	setCurrentHeading(heading: string) {
		this.getGlideBarState().currentHeading = heading;
		
		const activeBlock = contentManager.getDesignContent().find(
			(block:IDesignBlock) => block.contentBlock.contentBlockId === this.getGlideBarState().contentBlockId
		);
		
		if (activeBlock?.contentBlock.blockType === "heading") {
			const content = activeBlock.contentBlock.blockData as IHeadingBlockData;
			content.headingLevel = parseInt(heading.replace("H", ""));
			
			const index = contentManager.getDesignContent().findIndex(
				(blk:IDesignBlock) => blk.contentBlock.contentBlockId === activeBlock.contentBlock.contentBlockId
			);
			
			if (index !== -1) {
				contentManager.getDesignContent().splice(index, 1, {
					...activeBlock,
					contentBlock: activeBlock.contentBlock,
				});
			}
		}
	}
	
	/**
	 * Get the current heading level.
	 * @returns Returns a string value of the current heading level (e.g. `"H1"`, `"H2"`, `"H3"`, etc)
	 */
	getCurrentHeading(): string {
		return this.getGlideBarState().currentHeading;
	}
}



const glideBarManager = new GlideBarManager();
export default glideBarManager;